import * as React from 'react';
import { Checkbox, Form } from 'antd';

require('./ProjectCascadeDueDatesField.less');

// Whether moving a deliverable's due date also moves everything that depends on it
// (API field: cascadeDependentDueDates). Shared by ProjectCreateForm and
// ProjectEditForm, which are otherwise near duplicates where a pasted field would
// drift — same reason ProjectInternalTeamsField exists.
//
// Cascading is what projects do today, unconditionally, so the default is on and an
// existing project with no stored value keeps behaving exactly as before.
export const CASCADE_DUE_DATES_FIELD = 'cascadeDependentDueDates';

// Reads the setting off a project record. Direct index, NOT nullSafeGet/OrElse:
// those treat every falsy value as absent, so a project with cascading switched off
// would read back as switched on — which is how a stale "N dependents will move"
// warning got shown for a cascade-off project. Absent (older projects, or a backend
// that doesn't store it yet) means on, the behaviour before the setting existed.
export const getCascadeDueDatesValue = (project: any): boolean => {
	const value = project ? project[CASCADE_DUE_DATES_FIELD] : undefined;
	return typeof value === 'boolean' ? value : true;
};

export const getCascadeDueDatesInitialValues = (formData: any): { [key: string]: boolean } => ({
	[CASCADE_DUE_DATES_FIELD]: getCascadeDueDatesValue(formData),
});

// The value to send. `undefined` means "don't send it", which the API reads as "keep
// what is stored" (ProjectController: the field falls back to the stored project,
// then to true) — so a project edited while the checkbox isn't rendered, because the
// project has no deliverables, keeps its setting instead of being reset to on.
export const getCascadeDueDatesFromValues = (values: any): boolean | undefined => {
	// Direct index, not nullSafeGet: that treats every falsy value as absent, which
	// would silently drop an explicit `false` — the whole point of the setting.
	const value = values ? values[CASCADE_DUE_DATES_FIELD] : undefined;
	return typeof value === 'boolean' ? value : undefined;
};

interface ProjectCascadeDueDatesFieldProps {
	// Cascading only means something between deliverables, so the field is rendered
	// only when the project declares that entity.
	hasDeliverables: boolean;
}

// Only rendered for a project that has deliverables — dependencies, and therefore
// cascading, exist only between them.
const ProjectCascadeDueDatesField: React.FC<ProjectCascadeDueDatesFieldProps> = ({
	hasDeliverables,
}) =>
	hasDeliverables ? (
		// The explanation belongs to the checkbox, so it goes in `extra` — antd renders
		// that tight under the control, where a sibling div sits a full Form.Item margin
		// away and reads as unrelated (same reason the deliverable form's cascade warning
		// uses `extra`). A Tailwind margin can't close that gap either: antd's CSS loads
		// after tailwind.out.css, so plain `mb-*` on a Form.Item loses to
		// `.ant-form-item`'s own margin and needs the `!` modifier to win.
		<Form.Item
			name={CASCADE_DUE_DATES_FIELD}
			valuePropName="checked"
			className="cascade-due-dates-field !mb-4"
			extra={
				// `!` on the colour for the same reason: .ant-form-item-extra sets its own,
				// and it would win by source order.
				<span className="footnoteregular block max-w-[640px] !text-character-secondary">
					When a deliverable's due date moves, every deliverable waiting on it moves by the same
					number of days. Turn this off to move dates one at a time.
				</span>
			}
		>
			<Checkbox>Cascade dependent due dates</Checkbox>
		</Form.Item>
	) : null;

export default ProjectCascadeDueDatesField;
